import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect } from "react-router-dom";
import { Routes } from "../routes";

// pages
import DashboardOverview from "./dashboard/DashboardOverview";
import Transactions from "./Transactions";
import Settings from "./Settings";
import Welcome from "./Welcome";
import NotFoundPage from "./examples/NotFound";
import Company from "./company/Company";
import Employee from "./employee/Employee";
import EditOrganisation from "./editorganization/EditOrganisation";
import AddEntities from "./addentities/AddEntities";
import AddDepartment from "./addentities/AddDepartment";
import EditDepartment from "./addentities/EditDepartment";
import AddWorkPlaces from "./addworkplaces/AddWorkPlaces";
import EditWorkPlaces from "./addworkplaces/EditWorkPlaces";
import PermissionGroup from "./permissions/PermissionGroup";
import AddPermissionGroup from "./permissions/AddPermissionGroup";
import Brand from "./brand/brand";
import AddBrand from "./brand/AddBrand";
import EditBrand from "./brand/EditBrand";
import Category from "./category/category";
import AddCategory from "./category/AddCategory";
import EditCategory from "./category/EditCategory";
import Spusku from "./spusku/spusku";
import AddSpu from "./spusku/AddSpu";
import EditSpu from "./spusku/EditSpu";
import SkuPresets from "./skupresets/SkuPresets";
import BulkImport from './skupresets/BulkImport';
import Branch from './branch/branch';
import AddBranch from './branch/AddBranch';
import EditBranch from './branch/EditBranch';
import StockList from './stocklist/StockList';
import StockListV1 from './stocklist/StockListV1';
import ListingPricing from './listingpricing/ListingPricing';
import ListingPricingTest from './listingpricing/ListingPricingTest';
import PlatformListingTest from './listingpricing/PlatformListingTest';
import PlatformListingFetchTest from './listingpricing/PlatformListingFetchTest';
import AddEditPlatformListing from './listingpricing/AddEditPlatformListing';
import AddListing from './listingpricing/AddListing';
import EditListing from './listingpricing/EditListing';
import BulkPricing from './listingpricing/BulkPricing';
import PlatformOrder from './platformorder/PlatformOrder';
import LinkStockInstance from './platformorder/LinkStockInstance';
import PlatformOrderLinked from './platformorder/PlatformOrderLinked';
import PlatformOrderPage from './platformorder/PlatformOrderPage';
import PlatformOrderPageTest from './platformorder/PlatformOrderPageTest';
import PlatformManageOrder from './platformorder/PlatformManageOrder';
import PlatformManageOrderTest from './platformorder/PlatformManageOrderTest';

import AbbreviationsDictionary from './skuscheme/AbbreviationsDictionary';
import SkuScheme from './skuscheme/SkuScheme';
import SpuSkuPage from './spusku/SpuSkuPage';
import StockInstancePage from './stocklist/StockInstancePage';
import PlatformPricing from './platformpricing/PlatformPricing';
import PlatformBulkPricing from './platformpricing/BulkPricing';
import TradeInListing from './tradeinlisting/TradeInListing';
import TradeInListingTest from './tradeinlisting/TradeInListingTest';
import InitiatePricing from './tradeinpricing/InitiatePricing';
import PromotionalCampaign from './tradeinpricing/PromotionalCampaign';
import PromotionalCampaignTest from './tradeinpricing/PromotionalCampaignTest';
import EditCampaign from './tradeinpricing/EditCampaign';
import PricingHistory from './tradeinpricing/PricingHistory';
import PricingHistoryTest from './tradeinpricing/PricingHistoryTest';
import TradeInListingItems from './tradeinlisting/TradeInListingItems';
import AddEditTradeInListing from './tradeinlisting/AddEditTradeInListing';
import AddEditTradeInListingStep2 from './tradeinlisting/AddEditTradeInListingStep2';
import AddEditTradeInListingStep3 from './tradeinlisting/AddEditTradeInListingStep3';
import PricingTemplate from './pricingtemplate/PricingTemplate';
import AddPricingTemplate from './pricingtemplate/AddPricingTemplate';

// docs
import DocsOverview from "./documentation/DocsOverview";
import DocsDownload from "./documentation/DocsDownload";

// components
import Sidebar from "../components/Sidebar";
import Signin from "../components/Signin";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Department from './department/Department';
import AddEmployee from "./employee/AddEmployee";
import EditEmployee from "./employee/EditEmployee";
import EditEntities from "./addentities/EditEntities";

const RouteWithLoader = ({ component: Component, ...rest }) => (
  <Route {...rest} render={props => <Component {...props} />} />
);

const RouteWithNavbar = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={props => (
      <main className="content">
        <Navbar />
        <Component {...props} />
        <Footer />
      </main>
    )}
  />
);

const RouteWithSidebar = ({ component: Component, ...rest }) => {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 1023);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 767;
      const isTablet = window.innerWidth > 767 && window.innerWidth <= 1023;
      setCollapsed(isTablet || isMobile);
      if (!isMobile) setShowMobileSidebar(false);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMobileSidebar = () => setShowMobileSidebar(prev => !prev);
  const toggleCollapse = () => setCollapsed(prev => !prev);

  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem('settingsVisible') === 'false' ? false : true;
  };

  const [showSettings, setShowSettings] = useState(localStorageIsSettingsVisible());
  const toggleSettings = () => {
    const newValue = !showSettings;
    setShowSettings(newValue);
    localStorage.setItem('settingsVisible', newValue);
  };

  // ✅ Authentication check
  const token = localStorage.getItem('token');
  if (!token) {
    return <Redirect to="/" />;
  }

  return (
    <Route
      {...rest}
      render={props => (
        <>
          <Sidebar
            showMobile={showMobileSidebar}
            toggleMobile={toggleMobileSidebar}
            collapsed={collapsed}
            toggleCollapse={toggleCollapse}
          />
          <main className="content" style={{
            height: '100vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Navbar onMobileToggleClick={toggleMobileSidebar} />
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <Component {...props} />
              <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
            </div>
          </main>
        </>
      )}
    />
  );
};

export default () => (
  <Switch>
    {/* dashboard and other pages with sidebar and navbar */}
    <RouteWithSidebar exact path={Routes.DashboardOverview.path} component={DashboardOverview} />
    <RouteWithSidebar exact path={Routes.Transactions.path} component={Transactions} />
    <RouteWithSidebar exact path={Routes.Settings.path} component={Settings} />
    <RouteWithSidebar exact path={Routes.Company.path} component={Company} />
    <RouteWithSidebar exact path={Routes.Employee.path} component={Employee} />
    <RouteWithSidebar exact path={Routes.Department.path} component={Department} />
    <RouteWithSidebar exact path={Routes.EditOrganisation.path} component={EditOrganisation} />
    <RouteWithSidebar exact path={Routes.AddEntities.path} component={AddEntities} />
    <RouteWithSidebar exact path={Routes.AddDepartment.path} component={AddDepartment} />
    <RouteWithSidebar exact path={Routes.EditDepartment.path} component={EditDepartment} />
    <RouteWithSidebar exact path={Routes.AddEmployee.path} component={AddEmployee} />
    <RouteWithSidebar exact path={Routes.AddWorkPlaces.path} component={AddWorkPlaces} />
    <RouteWithSidebar exact path={Routes.EditWorkPlaces.path} component={EditWorkPlaces} />
    <RouteWithSidebar exact path={Routes.PermissionGroup.path} component={PermissionGroup} />
    <RouteWithSidebar exact path={Routes.AddPermissionGroup.path} component={AddPermissionGroup} />
    <RouteWithSidebar exact path={Routes.EditEmployee.path} component={EditEmployee} />
    <RouteWithSidebar exact path={Routes.EditEntities.path} component={EditEntities} />
    <RouteWithSidebar exact path={Routes.Brand.path} component={Brand} />
    <RouteWithSidebar exact path={Routes.AddBrand.path} component={AddBrand} />
    <RouteWithSidebar exact path={Routes.EditBrand.path} component={EditBrand} />
    <RouteWithSidebar exact path={Routes.Category.path} component={Category} />
    <RouteWithSidebar exact path={Routes.AddCategory.path} component={AddCategory} />
    <RouteWithSidebar exact path={Routes.EditCategory.path} component={EditCategory} />
    <RouteWithSidebar exact path={Routes.Spusku.path} component={Spusku} />
    <RouteWithSidebar exact path={Routes.AddSpu.path} component={AddSpu} />
    <RouteWithSidebar exact path={Routes.EditSpu.path} component={EditSpu} />
    <RouteWithSidebar exact path={Routes.SkuPresets.path} component={SkuPresets} />
    <RouteWithSidebar exact path={Routes.BulkImport.path} component={BulkImport} />
    <RouteWithSidebar exact path={Routes.Branch.path} component={Branch} />
    <RouteWithSidebar exact path={Routes.AddBranch.path} component={AddBranch} />
    <RouteWithSidebar exact path={Routes.EditBranch.path} component={EditBranch} />
    <RouteWithSidebar exact path={Routes.StockList.path} component={StockList} />
    <RouteWithSidebar exact path={Routes.StockListTest.path} component={StockListV1} />
    <RouteWithSidebar exact path={Routes.ListingPricing.path} component={PlatformListingTest} />
    <RouteWithSidebar exact path={Routes.ListingPricingTest.path} component={ListingPricingTest} />
    <RouteWithSidebar exact path={Routes.PlatformListingTest.path} component={PlatformListingTest} />
    <RouteWithSidebar exact path={Routes.PlatformListingFetchTest.path} component={PlatformListingFetchTest} />
    <RouteWithSidebar exact path={Routes.AddEditPlatformListing.path} component={AddListing} />
    <RouteWithSidebar exact path={Routes.AddListing.path} component={AddEditPlatformListing} />
    <RouteWithSidebar exact path={Routes.EditListing.path} component={AddEditPlatformListing} />
    <RouteWithSidebar exact path={Routes.BulkPricing.path} component={BulkPricing} />
    <RouteWithSidebar exact path={Routes.PlatformOrder.path} component={PlatformOrder} />
    <RouteWithSidebar exact path={Routes.LinkStockInstance.path} component={LinkStockInstance} />
    <RouteWithSidebar exact path={Routes.PlatformOrderLinked.path} component={PlatformOrderLinked} />
    <RouteWithSidebar exact path={Routes.PlatformOrderShopee.path} component={PlatformOrderPage} />
    <RouteWithSidebar exact path={Routes.PlatformOrderLazada.path} component={PlatformOrderPage} />
    <RouteWithSidebar exact path={Routes.PlatformOrderTiktok.path} component={PlatformOrderPage} />
    <RouteWithSidebar exact path={Routes.PlatformOrderShopeeTest.path} component={PlatformOrderPageTest} />
    <RouteWithSidebar exact path={Routes.PlatformOrderLazadaTest.path} component={PlatformOrderPageTest} />
    <RouteWithSidebar exact path={Routes.PlatformOrderTiktokTest.path} component={PlatformOrderPageTest} />
    <RouteWithSidebar exact path={Routes.PlatformManageOrder.path} component={PlatformManageOrder} />
    <RouteWithSidebar exact path={Routes.PlatformManageOrderTest.path} component={PlatformManageOrderTest} />

    <RouteWithSidebar exact path={Routes.AbbreviationsDictionary.path} component={AbbreviationsDictionary} />
    <RouteWithSidebar exact path={Routes.SkuScheme.path} component={SkuScheme} />
    <RouteWithSidebar exact path = {Routes.SpuSkuPage.path} component={SpuSkuPage}/>
    <RouteWithSidebar exact path = {Routes.StockInstancePage.path} component={StockInstancePage}/>
    <RouteWithSidebar exact path={Routes.PlatformPricing.path} component={PlatformPricing} />
    <RouteWithSidebar exact path={Routes.PlatformBulkPricing.path} component={PlatformBulkPricing} />
    <RouteWithSidebar exact path={Routes.TradeInListing.path} component={TradeInListing} />
    <RouteWithSidebar exact path={Routes.TradeInListingTest.path} component={TradeInListingTest} />
    <RouteWithSidebar exact path={Routes.TradeInListingItems.path} component={TradeInListingItems} />
    <RouteWithSidebar exact path={Routes.AddTradeInListing.path} component={AddEditTradeInListing} />
    <RouteWithSidebar exact path={Routes.EditTradeInListing.path} component={AddEditTradeInListing} />
    <RouteWithSidebar exact path={Routes.AddTradeInListingStep2.path} component={AddEditTradeInListingStep2} />
    <RouteWithSidebar exact path={Routes.AddTradeInListingStep3.path} component={AddEditTradeInListingStep3} />
    <RouteWithSidebar exact path={Routes.EditTradeInListingStep2.path} component={AddEditTradeInListingStep2} />
    <RouteWithSidebar exact path={Routes.EditTradeInListingStep3.path} component={AddEditTradeInListingStep3} />
    <RouteWithSidebar exact path={Routes.TradeInPricing.path} component={InitiatePricing} />
    <RouteWithSidebar exact path={Routes.TradeInPricingPromotional.path} component={PromotionalCampaign} />
    <RouteWithSidebar exact path={Routes.TradeInPricingPromotionalTest.path} component={PromotionalCampaignTest} />
    <RouteWithSidebar exact path={Routes.TradeInPricingEditCampaign.path} component={EditCampaign} />
    <RouteWithSidebar exact path={Routes.TradeInPricingHistory.path} component={PricingHistory} />
    <RouteWithSidebar exact path={Routes.TradeInPricingHistoryTest.path} component={PricingHistoryTest} />
    <RouteWithSidebar exact path={Routes.PricingTemplate.path} component={PricingTemplate} />
    <RouteWithSidebar exact path={Routes.AddPricingTemplate.path} component={AddPricingTemplate} />

    {/* signin page WITHOUT sidebar and navbar */}
    <Route exact path={Routes.Signin.path} component={Signin} />

    {/* Welcome page WITH navbar only */}
    <RouteWithNavbar exact path={Routes.Welcome.path} component={Welcome} />

    {/* docs pages with sidebar and navbar */}
    <RouteWithSidebar exact path={Routes.DocsOverview.path} component={DocsOverview} />
    <RouteWithSidebar exact path={Routes.DocsDownload.path} component={DocsDownload} />

    {/* fallback */}
    <RouteWithSidebar component={NotFoundPage} />
  </Switch>
);